import {
	Anchor,
	DateString,
	FormattedText
 } from "client/Components";
import { EmailImportance } from "client/Api/Email/common";
import { EmailActions } from "client/Api/Email/email";
import { isNotNilOrEmpty, setTrackingId } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { EmailDate } from "../EmailDate/EmailDate";
import { RecipientList } from "../RecipientList/RecipientList";
import styles from "./EmailHeaderDetails.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export type EmailHeaderDetailsProps = {
	cc: string;
	hasInlineImages: boolean;
	shouldAllowInlineImages: boolean;
	onAllowInlineImages(): void;
	importance: EmailImportance;
	lastAction: EmailActions;
	lastActionDateTime: string | Date | null;
	to: string;
	isMobile: boolean;
	showFullHeaderDetails: boolean;
	receivedDateTime: string | Date | null;
	timezone: TimeZone;
};

export const EmailHeaderDetails = (props: EmailHeaderDetailsProps): ReactElement => {
	const {
		cc,
		hasInlineImages,
		shouldAllowInlineImages,
		onAllowInlineImages,
		importance,
		lastAction,
		lastActionDateTime,
		to,
		isMobile,
		showFullHeaderDetails,
		receivedDateTime,
		timezone
	} = props;

	return (
		<>
			{isNotNilOrEmpty(to) && (
				<RecipientList
					value={to}
					wrappingBehavior={(isMobile && !showFullHeaderDetails) ? "ellipsis" : "wrap"}
					trackingId="to"
					label={localize("_To")}
				/>
			)}
			{isNotNilOrEmpty(cc) && (
				<RecipientList
					value={cc}
					wrappingBehavior="wrap"
					trackingId="cc"
					label={localize("_Cc")}
				/>
			)}
			{showFullHeaderDetails && (
				<div className={styles.emailDate}>
					<EmailDate
						value={receivedDateTime}
						timezone={timezone}
					/>
				</div>
			)}
			{hasInlineImages && !shouldAllowInlineImages && (
				<div className={styles.showInlineImages}>
					<Anchor
						onClick={onAllowInlineImages}
						trackingId="show-inline-images"
					>
						{localize("_EmailShowImage")}
					</Anchor>
				</div>
			)}
			{importance !== EmailImportance.Normal && (
				<div
					{...setTrackingId("importance")}
				>
					{localize(importanceToLocalizationKey[importance])}
				</div>
			)}
			{lastAction !== EmailActions.No && (
				<div
					{...setTrackingId("last-action")}
				>
					<FormattedText
						text={localize(actionToLocalizationKey[lastAction])}
						thingsToReplace={[
							{
								what: "{0}",
								filler: (
									<DateString
										value={lastActionDateTime ?? ""}
										format="DM(Y)"
										timezone={timezone}
									/>
								)
							}
						]}
					/>
				</div>
			)}
		</>
	);
};
EmailHeaderDetails.displayName = "emailHeaderDetails";

const importanceToLocalizationKey: {[key in Exclude<EmailImportance, EmailImportance.Normal>]: LocalizationKey} = {
	[EmailImportance.High]: "_ThisMessageWasSentWithHighImportance",
	[EmailImportance.Low]: "_ThisMessageWasSentWithLowImportance"
};

const actionToLocalizationKey: {[key in Exclude<EmailActions, EmailActions.No>]: LocalizationKey} = {
	[EmailActions.Forwarded]: "_YouForwardedThisEmailOn",
	[EmailActions.Replied]: "_YouRepliedToThisEmailOn"
};